import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Printer, Truck } from 'lucide-react';
import { formatTimeDisplay, isPlannerOrAbove, orderedTimelineSections } from '@peles/shared';
import { useTimeline, useUpdateTimeline } from '../hooks/useEventOps';
import { useEvent, useEventTags } from '../hooks/useEvents';
import { getAppLogoUrl } from '../api/events';
import { useAuth } from '../context/AuthContext';
import { TimelineEditor } from '../components/timeline/TimelineEditor';
import { TimelinePrintView } from '../components/timeline/TimelinePrintView';
import { VendorTimesModal } from '../components/timeline/VendorTimesModal';
import { TimelineEditorSkeleton } from '../components/timeline/TimelineEditorSkeleton';
import { timeToInputValue } from '../components/timeline/entryForm';

// Re-exported for existing consumers/tests; the implementations live in shared helpers.
export { formatTimeDisplay, timeToInputValue };

const HEADER_BUTTON =
  'flex items-center gap-1.5 min-h-11 sm:min-h-0 px-3 py-1.5 border border-gray-200 bg-white text-gray-700 text-sm rounded-lg hover:bg-gray-50';

/** An event's timeline (`/events/:eventId/timeline/:timelineId`), or one on its own (`/timelines/:timelineId`). */
export function TimelineEditPage() {
  const { timelineId, eventId } = useParams<{ timelineId: string; eventId: string }>();
  const { data: timeline, isLoading } = useTimeline(timelineId!);
  const { data: eventTags = [] } = useEventTags(eventId);
  const { data: event } = useEvent(eventId ?? '');
  const { data: appLogoUrl } = useQuery({
    queryKey: ['app-logo-url'],
    queryFn: getAppLogoUrl,
    staleTime: 60_000,
  });
  const updateTimeline = useUpdateTimeline();
  const { user } = useAuth();
  const isProducer = !!user && isPlannerOrAbove(user.systemRole);
  const [showPrint, setShowPrint] = useState(false);
  const [showVendorTimes, setShowVendorTimes] = useState(false);

  if (isLoading) return <TimelineEditorSkeleton />;
  if (!timeline) {
    return (
      <div className="p-6 text-red-500" data-testid="not-found">
        Timeline not found.
      </div>
    );
  }

  const isTemplate = timeline.isTemplate;
  const backPath = eventId
    ? `/events/${eventId}/timeline`
    : isTemplate
      ? '/templates'
      : '/timelines';
  // Stored as midnight UTC of the day picked; read back the same day.
  const dateValue = timeline.eventDate
    ? new Date(timeline.eventDate).toISOString().slice(0, 10)
    : '';

  return (
    <>
      <TimelineEditor
        timeline={timeline}
        backPath={backPath}
        badge={isTemplate ? 'Timeline Template' : 'Timeline'}
        tags={eventId ? eventTags : []}
        readOnly={!isProducer}
        actions={
          <>
            {eventId && isProducer && !isTemplate && (
              <button
                type="button"
                onClick={() => setShowVendorTimes(true)}
                className={HEADER_BUTTON}
                title="Add the vendors' arrival and load-in times"
                data-testid="vendor-times-button"
              >
                <Truck size={15} aria-hidden />
                <span className="hidden sm:inline">Vendor times</span>
                <span className="sr-only sm:hidden">Vendor times</span>
              </button>
            )}
            {eventId && (
              <button
                type="button"
                onClick={() => setShowPrint(true)}
                className={HEADER_BUTTON}
                title="Print timeline"
                data-testid="print-timeline-button"
              >
                <Printer size={15} aria-hidden />
                Print
              </button>
            )}
          </>
        }
        details={
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <label
              htmlFor="timeline-date"
              className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2"
            >
              <CalendarDays size={14} aria-hidden />
              Date
            </label>
            <input
              id="timeline-date"
              type="date"
              value={dateValue}
              disabled={!isProducer}
              onChange={(e) =>
                updateTimeline.mutate({
                  id: timeline.id,
                  updates: {
                    eventDate: e.target.value ? new Date(e.target.value).toISOString() : null,
                  },
                })
              }
              className="w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              data-testid="timeline-date-input"
            />
            <p className="mt-1 text-xs text-gray-500">
              The day this run of show is for — useful when the event spans several days.
            </p>
          </div>
        }
      />
      {eventId && isProducer && !isTemplate && (
        <VendorTimesModal
          open={showVendorTimes}
          onClose={() => setShowVendorTimes(false)}
          timelineId={timeline.id}
          eventId={eventId}
          sections={orderedTimelineSections(timeline.sections)}
        />
      )}

      {showPrint && (
        <TimelinePrintView
          timeline={timeline}
          eventName={event?.name ?? 'Event'}
          eventTags={eventTags}
          appLogoUrl={appLogoUrl ?? null}
          eventLogoUrl={event?.logoUrl ?? null}
          onClose={() => setShowPrint(false)}
        />
      )}
    </>
  );
}
